import { useState } from 'react';
import type { DirectoryResponse, OrganisationalPlacementResponse, RoleListResponse, TeamsResponse } from '../../api/types';
import { apiPost, describeError } from '../../api/client';
import { useApiResource } from '../../hooks/useApiResource';
import { ErrorState, LoadingState } from '../StateMessage';
import { ActionFeedback, AdminForm, Field, useAction } from './adminShared';
import styles from './admin.module.css';

/**
 * Employee Organisational Placement — the admin surface for
 * `api/src/routes/organisationalPlacement.ts` (Organisational Movement
 * Foundation mission).
 *
 * ---------------------------------------------------------------------
 * WHAT THIS SECTION IS, AND WHAT IT DELIBERATELY IS NOT
 *
 * This changes which Team an employee currently belongs to, and which
 * Role they currently hold — nothing else. It is not a "move employee"
 * wizard: team and role are two separate, independently-submitted changes
 * (mirroring `employeeOrganisationalPlacementService.ts`'s own two
 * independent write functions), because the product treats a lateral team
 * transfer and a role change as unrelated events that happen to occur on
 * the same person. There is no field here for department (it is entirely
 * derived from the team) and no field for manager (it is entirely derived
 * from the team's own lead, already editable from `TeamsSection` above).
 *
 * Deliberately an ADMINISTRATIVE action, not a self-service edit
 * (mission Phase 16): it lives only in this admin area, behind an
 * explicit "Change team" / "Change role" control, and is never reachable
 * from an employee's own profile page.
 *
 * Employee, team and role selection all reuse existing reads (`GET
 * /employees`, `GET /admin/teams`, `GET /roles`) rather than a new search
 * surface — the same call `EmployeeIdentitySection` makes for its own
 * employee picker.
 * ---------------------------------------------------------------------
 */
export function EmployeePlacementSection() {
  const directory = useApiResource<DirectoryResponse>('/employees');
  const teams = useApiResource<TeamsResponse>('/admin/teams');
  const roles = useApiResource<RoleListResponse>('/roles');
  const [employeeId, setEmployeeId] = useState<string | null>(null);

  const loading = directory.status === 'loading' || teams.status === 'loading' || roles.status === 'loading';
  if (loading) return <LoadingState label="Loading organisational placement…" />;
  if (directory.status === 'failed') {
    return <ErrorState title="The employee list could not be loaded" detail={describeError(directory.error)} />;
  }
  if (teams.status === 'failed') {
    return <ErrorState title="The team list could not be loaded" detail={describeError(teams.error)} />;
  }
  if (roles.status === 'failed') {
    return <ErrorState title="The role list could not be loaded" detail={describeError(roles.error)} />;
  }

  const employees = directory.data.employees;
  // Retired teams/roles are real history, not a valid new placement — the
  // same distinction `RoleLibrarySection`'s requirement-subject picker
  // already draws for its own dropdowns.
  const activeTeams = teams.data.teams.filter((team) => team.status === 'active');
  const activeRoles = roles.data.roles.filter((role) => role.status === 'active');
  const selectedEmployeeId = employeeId ?? employees[0]?.id ?? null;

  return (
    <section className={styles.section} aria-label="Employee placement">
      <h2 className={styles.sectionTitle}>Employee placement</h2>
      <p className={styles.sectionNote}>
        Where an employee currently sits in the organisation structure — their team and their role. Changing either
        closes the current assignment (if any) and opens a new one; nothing is edited in place, and the full history
        stays on record. Department and manager are not set here — they follow automatically from the team.
      </p>

      {employees.length === 0 ? (
        <p className={styles.rowMeta}>No employees exist yet.</p>
      ) : (
        <>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="placement-employee">
              Employee
            </label>
            <select
              id="placement-employee"
              className={styles.input}
              value={selectedEmployeeId ?? ''}
              onChange={(event) => setEmployeeId(event.target.value)}
            >
              {employees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.fullName}
                </option>
              ))}
            </select>
          </div>

          {selectedEmployeeId ? (
            <PlacementDetail employeeId={selectedEmployeeId} teams={activeTeams} roles={activeRoles} />
          ) : null}
        </>
      )}
    </section>
  );
}

function PlacementDetail({
  employeeId,
  teams,
  roles,
}: {
  employeeId: string;
  teams: TeamsResponse['teams'];
  roles: RoleListResponse['roles'];
}) {
  const placement = useApiResource<OrganisationalPlacementResponse>(`/admin/employees/${employeeId}/placement`);
  const [changingTeam, setChangingTeam] = useState(false);
  const [changingRole, setChangingRole] = useState(false);

  if (placement.status === 'loading') return <LoadingState label="Loading current placement…" />;
  if (placement.status === 'failed') {
    return <ErrorState title="This employee's placement could not be loaded" detail={describeError(placement.error)} />;
  }

  const data = placement.data;

  function reload() {
    placement.reload();
  }

  return (
    <div className={styles.row}>
      <span className={styles.rowMeta}>
        Team: {data.team ? `${data.team.name} (${data.team.departmentName})` : 'None'}
      </span>
      <span className={styles.rowMeta}>Role: {data.role ? data.role.name : 'None'}</span>
      <span className={styles.rowMeta}>
        Manager: {data.manager ? data.manager.fullName ?? 'Unnamed' : 'None'}
      </span>

      <span className={styles.rowActions}>
        <button type="button" className={styles.secondaryButton} onClick={() => setChangingTeam((open) => !open)}>
          {changingTeam ? 'Close' : 'Change team'}
        </button>
        <button type="button" className={styles.secondaryButton} onClick={() => setChangingRole((open) => !open)}>
          {changingRole ? 'Close' : 'Change role'}
        </button>
      </span>

      {changingTeam ? (
        <ChangeTeamForm
          employeeId={employeeId}
          teams={teams}
          currentTeamId={data.team?.id ?? null}
          onDone={() => {
            setChangingTeam(false);
            reload();
          }}
        />
      ) : null}
      {changingRole ? (
        <ChangeRoleForm
          employeeId={employeeId}
          roles={roles}
          currentRoleId={data.role?.id ?? null}
          onDone={() => {
            setChangingRole(false);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function ChangeTeamForm({
  employeeId,
  teams,
  currentTeamId,
  onDone,
}: {
  employeeId: string;
  teams: TeamsResponse['teams'];
  currentTeamId: string | null;
  onDone: () => void;
}) {
  const [teamId, setTeamId] = useState(currentTeamId ?? teams[0]?.id ?? '');
  const [effectiveFrom, setEffectiveFrom] = useState(todayIso());
  const { state, run } = useAction();

  if (teams.length === 0) {
    return <p className={styles.hint}>No active teams exist.</p>;
  }

  async function submit() {
    const ok = await run(async () => {
      await apiPost(`/admin/employees/${employeeId}/team-assignment`, { teamId, effectiveFrom });
      return 'The team assignment was changed.';
    });
    if (ok) onDone();
  }

  return (
    <AdminForm title="Change placement — team" onSubmit={submit} submitLabel="Change team" busy={state.status === 'saving'}>
      <div className={styles.field}>
        <label className={styles.label} htmlFor={`placement-team-${employeeId}`}>
          New team
        </label>
        <select
          id={`placement-team-${employeeId}`}
          className={styles.input}
          value={teamId}
          onChange={(event) => setTeamId(event.target.value)}
        >
          {teams.map((team) => (
            <option key={team.id} value={team.id}>
              {team.name} ({team.departmentName})
            </option>
          ))}
        </select>
      </div>
      <Field
        id={`placement-team-from-${employeeId}`}
        label="Effective from (YYYY-MM-DD)"
        value={effectiveFrom}
        onChange={setEffectiveFrom}
        hint="Closes the current team assignment (if any) on this date and opens the new one."
      />
      <ActionFeedback state={state} />
    </AdminForm>
  );
}

function ChangeRoleForm({
  employeeId,
  roles,
  currentRoleId,
  onDone,
}: {
  employeeId: string;
  roles: RoleListResponse['roles'];
  currentRoleId: string | null;
  onDone: () => void;
}) {
  const [roleId, setRoleId] = useState(currentRoleId ?? roles[0]?.id ?? '');
  const [effectiveFrom, setEffectiveFrom] = useState(todayIso());
  const { state, run } = useAction();

  if (roles.length === 0) {
    return <p className={styles.hint}>No active roles exist.</p>;
  }

  async function submit() {
    const ok = await run(async () => {
      await apiPost(`/admin/employees/${employeeId}/role-assignment`, { roleId, effectiveFrom });
      return 'The role assignment was changed.';
    });
    if (ok) onDone();
  }

  return (
    <AdminForm title="Change placement — role" onSubmit={submit} submitLabel="Change role" busy={state.status === 'saving'}>
      <div className={styles.field}>
        <label className={styles.label} htmlFor={`placement-role-${employeeId}`}>
          New role
        </label>
        <select
          id={`placement-role-${employeeId}`}
          className={styles.input}
          value={roleId}
          onChange={(event) => setRoleId(event.target.value)}
        >
          {roles.map((role) => (
            <option key={role.id} value={role.id}>
              {role.name}
            </option>
          ))}
        </select>
      </div>
      <Field
        id={`placement-role-from-${employeeId}`}
        label="Effective from (YYYY-MM-DD)"
        value={effectiveFrom}
        onChange={setEffectiveFrom}
        hint="Closes the current role assignment (if any) on this date and opens the new one."
      />
      <ActionFeedback state={state} />
    </AdminForm>
  );
}
